// src/components/BackupManagement.js
import React, { useEffect, useState } from 'react';
import { firestore, functions } from '../firebase';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  orderBy,
  limit as limitFn,
} from 'firebase/firestore';
import './BackupManagement.css';

const backupEventDataManualCallable = httpsCallable(functions, 'backupEventDataManual');

/** Yield so React can commit `isCreating` and the browser can paint before heavy async work. */
function waitForPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

async function createBackup(eventId) {
  const res = await backupEventDataManualCallable({ eventId });
  const data = res.data || {};
  return { backupId: data.backupId || '(unknown id)' };
}

async function fetchBackupsForEvent(eventId) {
  const backupsQuery = query(
    collection(firestore, 'events', eventId, 'backups'),
    orderBy('createdAt', 'desc'),
    limitFn(20),
  );
  const snap = await getDocs(backupsQuery);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

const categoryLabels = {
  people: 'People',
  teams: 'Teams',
  loadOrder: 'Load order',
  skyScores: 'Sky scores',
  bonusScores: 'Bonus scores',
  bonusPuzzles: 'Bonus puzzles',
  bonusPuzzleCatalog: 'Bonus puzzle catalog (global)',
  eventManifest: 'Event manifest',
  highAltitudeManifest: 'High Altitude manifest',
  transactions: 'Transactions',
  furyLabConfig: 'Fury Lab config',
  furyLabAssignments: 'Fury Lab assignments',
};

function formatTimestamp(ts) {
  if (!ts) return '—';
  if (ts.toDate) {
    try {
      return ts.toDate().toLocaleString();
    } catch {
      return String(ts);
    }
  }
  if (ts.seconds != null) {
    const d = new Date(ts.seconds * 1000);
    return d.toLocaleString();
  }
  return String(ts);
}

const BackupManagement = () => {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [backups, setBackups] = useState([]);
  const [backupConfig, setBackupConfig] = useState(null);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [error, setError] = useState('');
  const [runningAction, setRunningAction] = useState(null); // { type, backupId?, category? }
  const [isCreating, setIsCreating] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'error'|'success', message }

  useEffect(() => {
    if (!toast) return undefined;
    const ms = toast.type === 'error' ? 6000 : 3500;
    const t = setTimeout(() => setToast(null), ms);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    async function fetchInitial() {
      try {
        setLoadingEvents(true);
        const [eventsSnap, cfgSnap, activeId] = await Promise.all([
          getDocs(collection(firestore, 'events')),
          getDoc(doc(firestore, '_config', 'backup')),
          getActiveEventId(),
        ]);
        const eventsData = eventsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEvents(eventsData);
        if (eventsData.length) {
          setSelectedEventId(
            resolveAdminDefaultEventId({ eventIdParam: '', activeEventId: activeId, events: eventsData })
          );
        }
        if (cfgSnap.exists()) {
          setBackupConfig(cfgSnap.data());
        }
      } catch (err) {
        setError(err.message || 'Failed to load events/backup config.');
      } finally {
        setLoadingEvents(false);
      }
    }
    fetchInitial();
  }, []);

  useEffect(() => {
    if (!selectedEventId) {
      setBackups([]);
      return;
    }
    async function loadBackups() {
      try {
        setLoadingBackups(true);
        setError('');
        const list = await fetchBackupsForEvent(selectedEventId);
        setBackups(list);
      } catch (err) {
        setError(err.message || 'Failed to load backups.');
      } finally {
        setLoadingBackups(false);
      }
    }
    loadBackups();
  }, [selectedEventId]);

  const selectedEvent = events.find((e) => e.id === selectedEventId) || null;

  const handleManualBackup = async () => {
    if (!selectedEventId || isCreating) return;
    setError('');
    setToast(null);
    setIsCreating(true);
    try {
      await waitForPaint();
      const { backupId } = await createBackup(selectedEventId);
      const list = await fetchBackupsForEvent(selectedEventId);
      setBackups(list);
      setToast({ type: 'success', message: `Backup created (ID: ${backupId}).` });
    } catch (err) {
      const details = err?.details;
      const detailsMsg =
        typeof details === 'string'
          ? details
          : details && typeof details === 'object' && 'message' in details
            ? details.message
            : '';
      const message =
        err?.message ||
        detailsMsg ||
        'Manual backup failed.';
      setToast({ type: 'error', message });
      console.error('Manual backup error:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleRestore = async (backup, categories) => {
    if (!selectedEventId || !backup?.id) return;
    const label = categories && categories.length === 1
      ? categoryLabels[categories[0]] || categories[0]
      : 'selected categories';
    const ok = window.confirm(
      `Restore ${label} from backup ${backup.id} for event "${selectedEvent?.title || selectedEventId}"?\n\n` +
      'This will overwrite current data for the chosen categories.',
    );
    if (!ok) return;

    setError('');
    setRunningAction({
      type: 'restore',
      backupId: backup.id,
      category: categories && categories.length === 1 ? categories[0] : 'multiple',
    });
    try {
      const restoreFn = httpsCallable(functions, 'restoreEventBackup');
      const res = await restoreFn({
        eventId: selectedEventId,
        backupId: backup.id,
        categories,
      });
      const data = res.data || {};
      const results = data.results || {};
      const pieces = [];
      Object.keys(results).forEach((key) => {
        const r = results[key];
        if (!r) return;
        const parts = [];
        if (typeof r.deleted === 'number') parts.push(`deleted ${r.deleted}`);
        if (typeof r.restored === 'number') parts.push(`restored ${r.restored}`);
        if (typeof r.updated === 'number') parts.push(`updated ${r.updated}`);
        pieces.push(`${categoryLabels[key] || key}: ${parts.join(', ')}`);
      });
      const summaryText = pieces.length ? pieces.join('\n') : 'See logs for details.';
      window.alert(`Restore completed:\n\n${summaryText}`);
    } catch (err) {
      setError(err.message || 'Restore failed.');
    } finally {
      setRunningAction(null);
    }
  };

  return (
    <div className="backup-management">
      <h2>Backups &amp; Restore</h2>
      {toast && (
        <div
          className={`backup-toast backup-toast--${toast.type}`}
          role={toast.type === 'error' ? 'alert' : 'status'}
          aria-live="polite"
        >
          {toast.message}
        </div>
      )}
      {error && <p className="backup-error">{error}</p>}

      <div className="backup-toolbar">
        <label>
          Event:{' '}
          <select
            value={selectedEventId}
            onChange={(e) => setSelectedEventId(e.target.value)}
            disabled={isCreating}
          >
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.title}
                {ev.isActive ? ' (active)' : ''}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          onClick={handleManualBackup}
          disabled={!selectedEventId || !!runningAction || isCreating}
          className="btn-fm-primary"
          aria-busy={isCreating}
          aria-label={isCreating ? 'Creating backup, please wait' : 'Create manual backup now'}
        >
          {isCreating ? 'Creating…' : 'Create manual backup now'}
        </button>
      </div>

      {backupConfig && (
        <div className="backup-config-summary">
          <strong>Scheduled backup config</strong>
          <div className="backup-config-row">
            <span>Mode:</span>
            <span>{backupConfig.mode || 'off'}</span>
          </div>
          <div className="backup-config-row">
            <span>EventId:</span>
            <span>{backupConfig.eventId || '—'}</span>
          </div>
          <div className="backup-config-row">
            <span>Categories:</span>
            <span>
              {Array.isArray(backupConfig.categories) && backupConfig.categories.length
                ? backupConfig.categories.map((c) => categoryLabels[c] || c).join(', ')
                : 'All (people, teams, load order, sky scores, bonus scores, bonus puzzles, bonus puzzle catalog, event manifest, transactions, Fury Lab config, Fury Lab assignments)'}
            </span>
          </div>
          <p className="backup-config-note">
            Change these values via the Firestore console (`_config/backup`) to switch
            between daily and hourly backups.
          </p>
        </div>
      )}

      <div className="backup-list">
        <h3>Recent backups</h3>
        {loadingEvents || loadingBackups ? (
          <p>Loading backups…</p>
        ) : !selectedEventId ? (
          <p>Select an event to view backups.</p>
        ) : backups.length === 0 ? (
          <p>No backups found for this event yet.</p>
        ) : (
          <table className="backup-table">
            <thead>
              <tr>
                <th>Created</th>
                <th>Mode</th>
                <th>Categories</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {backups.map((b) => {
                const cats = Array.isArray(b.categories) && b.categories.length
                  ? b.categories
                  : Object.keys(categoryLabels);
                return (
                  <tr key={b.id}>
                    <td>{formatTimestamp(b.createdAt)}</td>
                    <td>{b.mode || 'manual'}</td>
                    <td>
                      {cats.map((c) => (
                        <span key={c} className="backup-tag">
                          {categoryLabels[c] || c}
                        </span>
                      ))}
                    </td>
                    <td className="backup-actions-cell">
                      <button
                        type="button"
                        className="backup-action-button"
                        disabled={!!runningAction || isCreating}
                        onClick={() => handleRestore(b, ['people'])}
                      >
                        People
                      </button>
                      <button
                        type="button"
                        className="backup-action-button"
                        disabled={!!runningAction || isCreating}
                        onClick={() => handleRestore(b, ['teams'])}
                      >
                        Teams
                      </button>
                      <button
                        type="button"
                        className="backup-action-button"
                        disabled={!!runningAction || isCreating}
                        onClick={() => handleRestore(b, ['loadOrder'])}
                      >
                        Load order
                      </button>
                      <button
                        type="button"
                        className="backup-action-button"
                        disabled={!!runningAction || isCreating}
                        onClick={() => handleRestore(b, ['skyScores'])}
                      >
                        Sky scores
                      </button>
                      <button
                        type="button"
                        className="backup-action-button"
                        disabled={!!runningAction || isCreating}
                        onClick={() => handleRestore(b, ['bonusScores'])}
                      >
                        Bonus scores
                      </button>
                      <button
                        type="button"
                        className="backup-action-button"
                        disabled={!!runningAction || isCreating}
                        onClick={() => handleRestore(b, ['bonusPuzzles'])}
                      >
                        Bonus puzzles
                      </button>
                      <button
                        type="button"
                        className="backup-action-button"
                        disabled={!!runningAction || isCreating}
                        onClick={() => handleRestore(b, ['bonusPuzzleCatalog'])}
                      >
                        Puzzle catalog
                      </button>
                      <button
                        type="button"
                        className="backup-action-button"
                        disabled={!!runningAction || isCreating}
                        onClick={() => handleRestore(b, ['eventManifest'])}
                      >
                        Event manifest
                      </button>
                      <button
                        type="button"
                        className="backup-action-button"
                        disabled={!!runningAction || isCreating}
                        onClick={() => handleRestore(b, ['highAltitudeManifest'])}
                      >
                        High Altitude manifest
                      </button>
                      <button
                        type="button"
                        className="backup-action-button"
                        disabled={!!runningAction || isCreating}
                        onClick={() => handleRestore(b, ['transactions'])}
                      >
                        Transactions
                      </button>
                      <button
                        type="button"
                        className="backup-action-button"
                        disabled={!!runningAction || isCreating}
                        onClick={() => handleRestore(b, ['furyLabConfig'])}
                      >
                        Fury Lab config
                      </button>
                      <button
                        type="button"
                        className="backup-action-button"
                        disabled={!!runningAction || isCreating}
                        onClick={() => handleRestore(b, ['furyLabAssignments'])}
                      >
                        Fury Lab assignments
                      </button>
                      <button
                        type="button"
                        className="backup-action-button backup-action-all"
                        disabled={!!runningAction || isCreating}
                        onClick={() => handleRestore(b, null)}
                      >
                        Restore all
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default BackupManagement;

